import { Table, Pagination, Form, Spinner } from "react-bootstrap";
import CachedIcon from "mdi-react/CachedIcon";
import { useQuery } from "react-query";
import currency from "currency.js";
import { addDays, format, parse } from "date-fns";
import { Fragment, useEffect, useRef, useState } from "react";
import queryString from "query-string";
import { CSVLink } from "react-csv";
import { isEmpty, toUpper } from "lodash";
import { useMemo } from "react";

import { ExcelIcon, PDFIcon } from "../Icons";
import "./../../assets/scss/reports/cashbook.scss";
import useDebounce, {
  useCurrencies,
  useQueryParams,
  useScrollTop,
} from "../../utils/hooks";
import { useAuth } from "../../hooks/useAuth";
import { useDownloadExcel } from "../../hooks/useDownloadExcel";
import ModalLoader from "../utils/ModalLoader";
import NoTableItem from "../utils/NoTableItem";
import RsDateRangePicker from "../utils/RsDateRangePicker";
import {
  calculateAccounts,
  displayAccountType,
  formatDate,
} from "../../utils/helpers";
import { IsPrivileged } from "../DisplayChildElement";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import IncorrectAccountsData from "../SystemMessages/IncorrectAccountsData";

function GeneralLedger() {
  useScrollTop();
  const initialFilterParams = {
    startDate: format(new Date(), "yyyy-MM-dd"),
    endDate: format(new Date(), "yyyy-MM-dd"),
    showBank: "",
  };

  const navigate = useNavigate();

  const { backendUrl } = useAuth();
  const [excelData, setExcelData] = useState([]);
  const {
    deploymentCurrencies: currenciesOptions,
    prevailingCurrency,
    getCurrencySymbol,
  } = useCurrencies();
  const CSVLinkRef = useRef(null);

  // fetch excel hook
  const [isfetchingExcel, fetchExcelData] = useDownloadExcel(
    excelData,
    CSVLinkRef
  );

  const [queryParams, setQueryParams] = useQueryParams({
    ...initialFilterParams,
    currency: "",
    convertCurrency: prevailingCurrency,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);
  useEffect(() => {
    setQueryParams({
      ...queryParams,
      ...debouncedFilterParams,
    });
  }, [debouncedFilterParams]);

  const fetchCustomerLedgers = async queryParams => {
    // await waitFor(5000);
    let response = await fetch(
      `${backendUrl}/api/reports/general-ledger?&${queryString.stringify(
        queryParams
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    // -----------------------

    data.accountTypes = [
      {
        label: "All Account Types",
        value: "",
      },
      ...data.accountTypes.map(el => ({
        ...el,
        label: toUpper(el.Type),
        value: el.Type,
      })),
    ];
    data.accountDetailTypes = [
      {
        label: "All Detail Types",
        value: "",
      },
      ...data.accountDetailTypes.map(el => ({
        ...el,
        label: toUpper(el.DetailType),
        value: el.DetailType,
      })),
    ];

    // =======================

    const types = [...new Set(data.journal.map(d => d.Description))];
    let allAccouts = {};
    types.forEach(el => {
      allAccouts[el] = data.journal.filter(d => d.Description === el);
      allAccouts[el]["key"] = [
        ...new Set(allAccouts[el].map(d => d.AccountID)),
      ][0];
      allAccouts[el]["EndingBalance"] = data.EndingBalance.filter(function(e) {
        return e.name === el;
      }, types);
    });

    data.allAccouts = Object.entries(allAccouts).map(el => [
      el[0],
      el[1].sort(
        (e, k) => Number(new Date(e.DateLog)) - Number(new Date(k.DateLog))
      ),
    ]);

    console.log(data.allAccouts);
    return data;
  };

  const {
    data,
    refetch,
    isFetching,
    isLoading,
    // isSuccess,
  } = useQuery(
    ["GENERAL_LEDGER", queryParams],
    () => fetchCustomerLedgers(queryParams),
    {
      keepPreviousData: true,
    }
  );

  const handleFilterParamsChange = e => {
    setFilterParams({
      ...filterParams,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  // download excel function
  const onDownloadExcelData = async () => {
    let exData = await fetchExcelData(
      `${backendUrl}/api/reports/general-ledger?${queryString.stringify(
        queryParams
      )}`,
      "GET"
    );

    const { company, journal } = exData.data;
    const types = [...new Set(journal.map(d => d.Description))];

    let allAccouts = {};
    types.forEach(el => {
      allAccouts[el] = data.journal.filter(d => d.Description === el);
      allAccouts[el]["key"] = [
        ...new Set(allAccouts[el].map(d => d.AccountID)),
      ][0];
      allAccouts[el]["EndingBalance"] = data.EndingBalance.filter(function(e) {
        return e.name === el;
      }, types);
    });

    allAccouts = Object.entries(allAccouts);

    allAccouts = allAccouts.map(el => {
      const mainData = el[1].map(d => [
        format(new Date(d?.DateLog), "dd MMM, yyyy"),
        d["AccountDescription.Type"],
        d.Description,
        d["AccountDescription.AccountID"],
        String(`${d.Ref}`.split("-")[0]),
        String(`${d.Ref}`.split("-")[1]),
        ...(filterParams.showBank
          ? [
              d?.paymentSources?.map(el =>
                el?.Description ? el?.Description : ""
              ),
            ]
          : []),
        currency(d.Debit, {
          symbol: currencySymbol,
        }).format(),
        currency(d.Credit, {
          symbol: currencySymbol,
        }).format(),
        currency(d.justCalculatedBalance, {
          symbol: currencySymbol,
        }).format(),
      ]);

      if (el[1].at(-1)) {
        mainData.unshift([
          queryParams?.startDate
            ? formatDate(addDays(new Date(queryParams?.startDate), 0))
            : "",
          el[1][0]["AccountDescription.Type"],
          el[1][0]["Description"],
          el[1][0]["AccountDescription.AccountID"],
          "",
          `Opening Balance at ${
            queryParams?.startDate
              ? formatDate(addDays(new Date(queryParams?.startDate), 0))
              : ""
          }`,
          ...(queryParams?.showBank ? [""] : []),
          currency(
            displayAccountType(
              el[1][0]["AccountDescription.Type"],
              calculateAccounts(
                el[1]["key"],
                el[1]["EndingBalance"][0].CreditJounalBackDate,
                el[1]["EndingBalance"][0].DebitJounalBackDate
              ),
              "debit"
            ),
            {
              symbol: currencySymbol,
              negativePattern: "(#)",
            }
          ).format(),
          currency(
            displayAccountType(
              el[1][0]["AccountDescription.Type"],
              calculateAccounts(
                el[1]["key"],
                el[1]["EndingBalance"][0].CreditJounalBackDate,
                el[1]["EndingBalance"][0].DebitJounalBackDate
              ),
              "credit"
            ),
            {
              symbol: currencySymbol,
              negativePattern: "(#)",
            }
          ).format(),
          "",
        ]);

        mainData.push([
          "",
          "",
          "",
          "",
          "",
          "Current Period Change",
          ...(filterParams.showBank ? [""] : []),
          currency(
            el[1].filter(el => el.isApproved).reduce((p, c) => p + c.Debit, 0),
            {
              symbol: currencySymbol,
            }
          ).format(),
          currency(
            el[1].filter(el => el.isApproved).reduce((p, c) => p + c.Credit, 0),
            {
              symbol: currencySymbol,
            }
          ).format(),
          currency(
            calculateAccounts(
              el[1]["key"],
              el[1]
                .filter(el => el.isApproved)
                .reduce((p, c) => p + c.Credit, 0),
              el[1].filter(el => el.isApproved).reduce((p, c) => p + c.Debit, 0)
            ),
            {
              symbol: currencySymbol,
            }
          ).format(),
        ]);
        mainData.push([
          "",
          "",
          "",
          "",
          "",
          "Ending Balance",
          ...(filterParams.showBank ? [""] : []),
          "",
          "",
          currency(
            calculateAccounts(
              el[1]["key"],
              el[1]["EndingBalance"][0].CreditJounal,
              el[1]["EndingBalance"][0].DebitJounal
            ),
            {
              symbol: currencySymbol,
            }
          ).format(),
        ]);
      }

      return mainData;
    });

    const spreadData = [];

    allAccouts.forEach(e => {
      e.forEach(d => {
        spreadData.push(d);
      });
    });

    const date =
      queryParams.startDate && queryParams.endDate
        ? `Date Prepared: Between ${format(
            new Date(queryParams.startDate),
            "E MMM d yyyy k:mm:ss z"
          )} to ${format(
            new Date(queryParams.endDate),
            "E MMM d yyyy k:mm:ss z"
          )}`
        : "";
    exData = [
      [company[0]?.CompName],
      ["General Ledger Report"],
      [date],
      [""],
      [
        "Date",
        "Account Type",
        "Account Description",
        "Account ID",
        "Reference",
        "Trans Description",
        ...(filterParams.showBank ? ["Payment Source"] : []),
        "Debit",
        "Credit",
        "Balance",
      ],
      ...spreadData,
    ];

    //  console.log(exData);
    setExcelData(exData);
  };

  const filterByDateRange = date => {
    setFilterParams({
      ...filterParams,
      startDate: format(date[0], "yyyy-MM-dd"),
      endDate: format(date[1], "yyyy-MM-dd"),
    });
  };

  const clearDateRange = () => {
    setFilterParams({
      ...filterParams,
      startDate: "",
      endDate: "",
    });
  };

  const currencySymbol = useMemo(() => {
    return getCurrencySymbol(
      filterParams.convertCurrency
        ? filterParams.convertCurrency
        : prevailingCurrency
    );
  }, [filterParams.convertCurrency, prevailingCurrency]);

  const openList = (e, el) => {
    if (e.detail === 2) {
      navigate(
        `/account-setup/account-list?AccountID=${
          el["AccountDescription.AccountID"]
        }&Description=${el?.Description}&startDate=${filterParams.startDate ||
          ""}&endDate=${filterParams.endDate || ""}`
      );
    }
  };

  const resolveBankClick = el => {
    return `/journal-entry/edit/${el.TransID}`;
    /*  console.log(el);
    const reqMatch = el.Ref?.match(/REQ\d+/)?.[0];

    return reqMatch
      ? `/requisition/request/preview/${reqMatch}?page=1&limit=40&q=&status=&type=&sortByDate=dateModified&sortByArchive=&is=&labelId=&attachedTransactionID=&filterBy=Title`
      : `/journal-entry/edit/${el.TransID}`; */
  };

  // isprivileged
  return (
    <IsPrivileged roleName="Financials">
      <IncorrectAccountsData />

      <main className="cash-book table-sticky-header">
        <div className="content">
          <header className="container pt-5 px-1 gap-0 text-nowrap">
            <h1>
              General Ledger Report
              <button
                title="Refresh"
                onClick={() => refetch()}
                className="btn text-primary"
              >
                <CachedIcon />
              </button>
            </h1>
            <div className="actions gap-2">
              <Form.Check
                inline
                label="Show payment source"
                name="showBank"
                type={"checkbox"}
                id="showBank"
                checked={filterParams?.showBank}
                onChange={e => handleFilterParamsChange(e)}
              />

              <CSVLink
                className="btn print d-none"
                filename={`General Ledger Report(${format(
                  new Date(),
                  "dd-MMM-yyyy hh:mm:ss a"
                )}).csv`}
                data={excelData}
                ref={CSVLinkRef}
              />

              <Form.Group className="position-relative">
                <Form.Label className="fw-5 position-absolute bottom-100 start-0 m-1">
                  Filter By
                </Form.Label>
                <Select
                  classNamePrefix="form-select"
                  placeholder="All Currencies"
                  isSearchable={true}
                  options={currenciesOptions}
                  value={currenciesOptions.find(
                    el => el.value == filterParams.currency
                  )}
                  onChange={selected =>
                    setFilterParams({
                      ...filterParams,
                      page: 1,
                      currency: selected?.value,
                    })
                  }
                  isClearable={true}
                />
              </Form.Group>

              <Form.Group className="position-relative">
                <Form.Label className="fw-5 position-absolute bottom-100 start-0 m-1">
                  Report In{" "}
                </Form.Label>
                <Select
                  classNamePrefix="form-select"
                  placeholder="Select Currency"
                  isSearchable={true}
                  options={currenciesOptions}
                  value={currenciesOptions.find(
                    el => el.value === filterParams.convertCurrency
                  )}
                  onChange={({ value }) =>
                    setFilterParams({
                      ...filterParams,
                      page: 1,
                      convertCurrency: value,
                    })
                  }
                />
              </Form.Group>

              <div className="position-relative">
                <RsDateRangePicker
                  placement="bottomEnd"
                  value={
                    filterParams.startDate && filterParams.endDate
                      ? [
                          parse(
                            filterParams.startDate,
                            "yyyy-MM-dd",
                            new Date()
                          ),
                          parse(filterParams.endDate, "yyyy-MM-dd", new Date()),
                        ]
                      : []
                  }
                  onClean={() => clearDateRange()}
                  onOk={date => filterByDateRange(date)}
                />
              </div>
              <a
                target="_blank"
                rel="noreferrer"
                href={`${backendUrl}/api/reports/pdf/general-ledger?${queryString.stringify(
                  {
                    ...queryParams,
                    currencySymbol,
                  }
                )}`}
                className="btn print"
              >
                PDF <PDFIcon color="#ff0000" />
              </a>
              <button className="btn print" onClick={onDownloadExcelData}>
                Excel <ExcelIcon color="#008000" />
              </button>
            </div>
          </header>

          <div className="container d-flex gap-3 px-0 align-items-center z-10 w-100">
            <div className="global-search-area col-md-4 text-nowrap">
              <MagnifyIcon />
              <Form.Control
                className=""
                name="Description"
                value={filterParams.Description}
                onChange={e => handleFilterParamsChange(e)}
                placeholder="Search Description..."
              />
            </div>

            <div className="col-md-3">
              <Select
                classNamePrefix={"form-select"}
                options={data?.accountTypes}
                value={data?.accountTypes?.find(
                  el => el.value === filterParams.Type
                )}
                onChange={selected =>
                  setFilterParams({
                    ...filterParams,
                    page: 1,
                    Type: selected.value,
                  })
                }
                placeholder="Account Type"
                isClearable
              />
            </div>

            {/*  <div className="col-md-3">
              <Select
                classNamePrefix={"form-select"}
                options={data?.accountDetailTypes}
                value={data?.accountDetailTypes?.find(el => el.value === filterParams.DetailType)}
                onChange={selected =>
                  setFilterParams({
                    ...filterParams,
                    page: 1,
                    DetailType: selected.value,
                  })
                }
              />
            </div> */}
          </div>

          <hr />

          <div>
            <Table
              // responsive
              borderless
              striped
              className="product-table container"
              hover
            >
              <thead className="position-sticky top-0">
                <tr>
                  <th>Date</th>
                  <th>Account Type</th>
                  <th>Account Description</th>
                  <th>Account ID</th>
                  <th>Reference</th>
                  <th>Trans Description</th>
                  {filterParams.showBank && <th>Payment Source</th>}
                  <th>Debit</th>
                  <th>Credit</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {data?.allAccouts?.map((el, index) => (
                  <Fragment key={index}>
                    {queryParams.startDate ? (
                      <>
                        {el[1].at(-1) && (
                          <tr>
                            <td className="text-nowrap fw-bold">
                              {queryParams.startDate
                                ? formatDate(
                                    addDays(new Date(queryParams.startDate), 0)
                                  )
                                : ""}
                            </td>
                            <td>{el[1][0]["AccountDescription.Type"]}</td>
                            <td>{el[1][0]["Description"]}</td>
                            <td>{el[1][0]["AccountDescription.AccountID"]}</td>
                            <td />
                            <td className="fw-bold">
                              {"Opening Balance at "}{" "}
                              {queryParams.startDate
                                ? formatDate(
                                    addDays(new Date(queryParams.startDate), 0)
                                  )
                                : ""}
                            </td>
                            {filterParams.showBank && <td />}
                            <td className="fw-bold">
                              {currency(
                                displayAccountType(
                                  el[1][0]["AccountDescription.Type"],
                                  calculateAccounts(
                                    el[1]["key"],
                                    el[1]["EndingBalance"][0]
                                      .CreditJounalBackDate,
                                    el[1]["EndingBalance"][0]
                                      .DebitJounalBackDate
                                  ),
                                  "debit"
                                ),
                                {
                                  symbol: currencySymbol,
                                  negativePattern: "(#)",
                                }
                              ).format()}
                            </td>
                            <td className="text-nowrap fw-bold">
                              {" "}
                              {currency(
                                displayAccountType(
                                  el[1][0]["AccountDescription.Type"],
                                  calculateAccounts(
                                    el[1]["key"],
                                    el[1]["EndingBalance"][0]
                                      .CreditJounalBackDate,
                                    el[1]["EndingBalance"][0]
                                      .DebitJounalBackDate
                                  ),
                                  "credit"
                                ),
                                {
                                  symbol: currencySymbol,
                                  negativePattern: "(#)",
                                }
                              ).format()}
                            </td>
                            <td className="text-nowrap fw-bold"> </td>
                          </tr>
                        )}
                      </>
                    ) : null}

                    {el[1].map((d, i) => (
                      <tr
                        key={i}
                        className={`${
                          !Boolean(d.isApproved)
                            ? "faded-cells-excluding-none"
                            : ""
                        }`}
                        onClick={e => openList(e, d)}
                      >
                        <td className="text-nowrap">
                          {format(new Date(d?.DateLog), "dd MMM, yyyy")}
                        </td>
                        <td>{d["AccountDescription.Type"]}</td>
                        <td>{d.Description}</td>
                        <td>{d["AccountDescription.AccountID"]}</td>
                        <td>{`${d.Ref}`.split("-")[0]}</td>
                        <td>{`${d.Ref}`.split("-")[1]}</td>
                        {filterParams.showBank && (
                          <td>
                            {d?.paymentSources?.map(el => (
                              <Link to={resolveBankClick(el)} target="_blank">
                                {el.Description}
                              </Link>
                            ))}
                          </td>
                        )}
                        <td className="text-nowrap">
                          {currency(d.Debit, {
                            symbol: currencySymbol,
                          }).format()}
                        </td>
                        <td className="text-nowrap">
                          {currency(d.Credit, {
                            symbol: currencySymbol,
                          }).format()}
                        </td>
                        <td className="text-nowrap">
                          {currency(d.justCalculatedBalance, {
                            symbol: currencySymbol,
                          }).format()}
                        </td>
                      </tr>
                    ))}

                    {el[1].at(-1) && (
                      <tr>
                        <td />
                        <td />
                        <td />
                        <td /> <td />
                        <td className="text-nowrap">Current Period Change</td>
                        {filterParams.showBank && <td />}
                        <td className="text-nowrap">
                          {currency(
                            el[1]
                              .filter(el => el.isApproved)
                              .reduce((p, c) => p + c.Debit, 0),
                            {
                              symbol: currencySymbol,
                            }
                          ).format()}
                        </td>
                        <td className="text-nowrap">
                          {currency(
                            el[1]
                              .filter(el => el.isApproved)
                              .reduce((p, c) => p + c.Credit, 0),
                            {
                              symbol: currencySymbol,
                            }
                          ).format()}
                        </td>
                        <td className="text-nowrap">
                          {currency(
                            calculateAccounts(
                              el[1]["key"],
                              el[1]
                                .filter(el => el.isApproved)
                                .reduce((p, c) => p + c.Credit, 0),
                              el[1]
                                .filter(el => el.isApproved)
                                .reduce((p, c) => p + c.Debit, 0)
                            ),
                            {
                              symbol: currencySymbol,
                            }
                          ).format()}
                        </td>
                      </tr>
                    )}

                    {el[1].at(-1) && (
                      <tr>
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />

                        <td className="fw-bold">Ending Balance</td>
                        {filterParams.showBank && <td />}
                        <td />
                        <td />
                        <td className="fw-bold text-nowrap">
                          {currency(
                            calculateAccounts(
                              el[1]["key"],
                              el[1]["EndingBalance"][0].CreditJounal,
                              el[1]["EndingBalance"][0].DebitJounal
                            ),
                            {
                              symbol: currencySymbol,
                            }
                          ).format()}
                        </td>
                      </tr>
                    )}

                    <tr>
                      <td colSpan={10} className="p-5" />
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </Table>
          </div>
          {!isFetching && isEmpty(data?.allAccouts) ? (
            <NoTableItem queryParams={queryParams} />
          ) : null}
          <div className="pagination">
            {/* Left */}
            {/* <div className="pagination_left">
            <p className="m-0 p-0">Show</p>
            <select name="rows" className="form-select ">
              <option value="10">10 rows</option>
              <option value="20">20 rows</option>
              <option value="30">30 rows</option>
              <option value="40">40 rows</option>
            </select>
          </div> */}

            {/* right */}
            {/* <div className="pagintion_right">
            <Pagination>
              <Pagination.Item active>{1}</Pagination.Item>
              <Pagination.Item>{2}</Pagination.Item>

              <Pagination.Ellipsis className="border-none" />

              <Pagination.Item>{6}</Pagination.Item>
              <Pagination.Next className="next" />
            </Pagination>
          </div> */}
          </div>
        </div>
        <ModalLoader show={isLoading || isfetchingExcel} />
      </main>
    </IsPrivileged>
  );
}

export default GeneralLedger;
